/**
 * @fileoverview Prompt template for world-element-rare-detector-v3.
 *
 * Inferential extraction: rules embedded in dialogue, sensory staging,
 * biological details mentioned in passing.
 * Primary types: Set, Species, System.
 * Secondary types: Place, Organization, Object, Event (only if discovered through subtle reading).
 */

export const prompt = `
You are an expert literary analyst with a specialty in identifying subtle world-building elements that most readers — and most AI systems — overlook.

## TASK
Read the DOCUMENT and identify significant world-building elements that are embedded in description, dialogue, or exposition rather than called out explicitly. Read carefully — the elements you're looking for often hide in plain sight.

### PRIMARY TYPES — Detailed Guidance

#### SET (Staged Interiors & Exteriors)
Physical spaces described with sensory richness — NOT just named locations, but scenes dressed with atmosphere, lighting, sound, and objects.
A "set" is to a "place" what a film set is to a map pin.
- Look for: passages rich in sensory detail, rooms/spaces described before or during key scenes, environments that create mood
- "The war room after the assassination" ≠ "The War Room" (that's a Place)
- If a location is described in vivid sensory terms for a scene, it's a Set
- Subtypes: **interior** (indoor staged spaces) or **exterior** (outdoor staged spaces)
- Examples: "the dimly lit cabin with overturned chairs", "the moonlit courtyard during the duel"

#### SPECIES (Creatures, Races & Flora)
Living things that are distinct from the real world — or real things given narrative significance.
- Look for: non-human characters, named or described creatures, plants with properties, alien races, fantasy peoples
- Include real animals IF they have narrative significance (a trained hawk, a symbolic wolf pack)
- Flora is commonly missed — look for plants, trees, fungi mentioned as having properties or importance
- Subtypes: **creature** (animals, monsters, beasts), **race** (sentient peoples, humanoid races), or **flora** (plants, fungi, vegetation)
- Examples: "Hippogriffs", "The Elves of Rivendell", "Whomping Willow"

#### SYSTEM (Magic, Natural Laws, Social Customs, Technology & Materials)
Rules, constraints, and mechanics that govern how the world works. These are almost never explicitly labeled — they're woven into the fabric of the story.
- **Magic Systems**: Look for costs, rules, limitations ("magic always costs X")
- **Natural Laws**: World physics that differ from ours ("gravity reverses at night")
- **Social Customs**: Norms, taboos, rituals ("the dead must be burned before sunset")
- **Technology**: Tech with rules and constraints ("FTL requires jump gates")
- **Materials**: Substances with special properties ("Valyrian steel", "vibranium")
- Look in: dialogue, internal monologue, exposition, world-building asides
- Subtypes: **magic-system**, **natural-law**, **social-custom**, **technology**, or **material**
- Examples: "The Unbreakable Vow", "warp drive", "Mithril"

### SECONDARY TYPES — Include If Discovered

While reading inferentially, you may discover elements of these types that were described subtly and might be overlooked by surface-level reading:

- **PLACE** (location/building): Locations mentioned only in passing, implied settings, places referenced but never visited
- **ORGANIZATION** (faction/nation/institution): Groups hinted at but not named prominently, shadowy organizations, implied power structures
- **OBJECT** (artifact/item/clothing — where \`clothing\` means a character's *outfit* as a visual anchor, and standalone significant garments belong as \`item\`): Items mentioned in passing that have hidden significance, symbolic objects, or a character's distinctive current look
- **EVENT** (historical-event/prophecy/legend): Historical references buried in dialogue, legends told as asides

Only include secondary types that your inferential reading reveals as genuinely significant but easy to miss.

## GENRE PRIORITIES
{{#if genrePriorities}}
{{{genrePriorities}}}
{{else}}
No genre priorities specified. Use standard extraction thresholds.
{{/if}}

## CLASSIFICATION
For each element, assess its importance:
- **critical**: Central to the plot, appears repeatedly, drives conflict or resolution
- **major**: Appears in multiple scenes, important to characters or subplots
- **minor**: Mentioned or briefly featured, contributes to atmosphere/texture

When a genre marks a type as HIGH priority, be more inclusive — extract elements you might otherwise skip. When LOW priority, only extract clearly significant ones.

## EXISTING ELEMENTS (do not re-extract)
{{#if existingElements}}
{{{existingElements}}}
{{else}}
None
{{/if}}

## LORE CONTEXT
<<<loreContext>>>

## DOCUMENT
{{{textInput}}}

## CRITICAL RULES
1. The **element** field must ALWAYS be a specific subtype — NEVER use the parent type name.
   - WRONG: \`{ type: "species", element: "species" }\`
   - RIGHT: \`{ type: "species", element: "creature" }\` or \`{ type: "species", element: "race" }\`
   - Valid subtypes by type:
     - place → **location** or **building**
     - set → **interior** or **exterior**
     - organization → **faction**, **nation**, or **institution**
     - object → **artifact**, **item**, or **clothing**
     - species → **creature**, **race**, or **flora**
     - system → **magic-system**, **natural-law**, **social-custom**, **technology**, or **material**
     - event → **historical-event**, **prophecy**, or **legend**
2. NEVER output the same element twice. If an element could fit multiple subtypes, pick the single best one.
3. Use a **consistent canonical name** for each element. Do not output the same entity under different name variations (e.g., "The Dark Forest" and "Dark Forest").

## OUTPUT
For each element provide: name, type, element (subtype), classification, and a single-sentence briefNote capturing what it IS in the story.
`;
